export function normalizeLoginEmail(email: string): string {
  return email.trim().toLowerCase();
}

export const loginCodeMinLength = 6;
export const loginCodeMaxLength = 10;

export function normalizeLoginCode(code: string): string {
  return code.replace(/\D/g, "").slice(0, loginCodeMaxLength);
}

export function isLoginCodeComplete(code: string): boolean {
  return normalizeLoginCode(code).length >= loginCodeMinLength;
}

export async function matchesConfiguredLoginEmailHash(email: string, configuredHash: string): Promise<boolean> {
  if (!configuredHash) return false;
  if (!globalThis.crypto?.subtle) return false;

  try {
    const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(email));
    const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    return hash === configuredHash;
  } catch {
    return false;
  }
}

type LoginErrorLike = {
  message?: string;
  status?: number;
  code?: string;
};

function retryDelayLabel(message: string): string {
  const match = message.match(/(?:after|in)\s+(\d+)\s*(second|minute|hour)s?/i);
  if (!match) return "";

  const amount = Number(match[1]);
  const unit = match[2].toLowerCase();
  if (!Number.isFinite(amount) || amount <= 0) return "";

  const label = amount === 1 ? unit : `${unit}s`;
  return `${amount} ${label}`;
}

export function loginErrorMessage(error: LoginErrorLike | null | undefined, fallback: string): string {
  const message = String(error?.message || "").trim();
  const errorCode = String(error?.code || "");
  const rateLimitText = `${message} ${errorCode}`;

  if (error?.status === 429 || /rate.?limit|too many requests/i.test(rateLimitText)) {
    const retryDelay = retryDelayLabel(message);
    if (retryDelay) {
      return `A sign-in code was just requested. Try the newest email code, or request another in about ${retryDelay}.`;
    }

    return "Supabase is throttling sign-in code requests. Try the newest email code, or wait a few minutes before requesting another one.";
  }

  if (/token.*(expired|invalid)|(expired|invalid).*token|invalid.*otp|otp.*invalid/i.test(message)) {
    return "That code is invalid or expired. Use the newest email code, or request a new one once the resend limit clears.";
  }

  return message || fallback;
}
